import type { Transaction, User } from 'payments-api/model';
import {
  useGetTransactions,
  usePostTransactionsIdAllocations,
} from 'api/payments/generated/fragmentPaymentsAPI';
import { useToast } from 'components/ToastManager';
import React, { useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { FormButtons } from '@fragment/ui/src/components/Button/ButtonGroup/FormButtons';
import { Modal } from '@fragment/ui/src/components/Modal/Modal';
import { ModalDropdown } from '@fragment/ui/src/components/Modal/ModalDropdown';
import { ModalField } from '@fragment/ui/src/components/Modal/ModalField';
import { Toast } from '@fragment/ui/src/components/Toast';

import type { ItemTableItem } from './ItemTable';

type AllocateTransactionModalProps = {
  invoiceId: string;
  items: ItemTableItem[];
  users: User[];
  onClose: VoidFunction;
};

export const AllocateTransactionModal = ({
  invoiceId,
  items,
  users,
  onClose,
}: AllocateTransactionModalProps) => {
  const { data } = useGetTransactions({
    reconciliation_status: 'unreconciled',
  });
  const postAllocationsMutation = usePostTransactionsIdAllocations();
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  const [selectedTransactionId, setSelectedTransactionId] = useState('');
  const [selectedUserId, setSelectedUserId] = useState('');

  const transactions = useMemo(() => data?.data ?? [], [data]);

  const transactionOptions = useMemo(
    () =>
      transactions.map(
        (txn) => `${txn.external_id} (${txn.amount} ${txn.currency})`
      ),
    [transactions]
  );

  const optionToTxn = useMemo(() => {
    const map: Record<string, Transaction> = {};
    transactions.forEach((txn, i) => {
      map[transactionOptions[i]] = txn;
    });
    return map;
  }, [transactions, transactionOptions]);

  const selectedTransaction = useMemo(
    () => transactions.find((t) => t.id === selectedTransactionId),
    [transactions, selectedTransactionId]
  );

  const selectedTransactionLabel = useMemo(() => {
    if (!selectedTransaction) return '';
    return `${selectedTransaction.external_id} (${selectedTransaction.amount} ${selectedTransaction.currency})`;
  }, [selectedTransaction]);

  // Filter users based on transaction amount sign:
  // positive → payin users, negative → payout users
  const userOptions = useMemo(() => {
    if (!selectedTransaction) return [];
    const isPositive = Number(selectedTransaction.amount) > 0;
    const relevantType = isPositive ? 'payin' : 'payout';
    const relevantUserIds = new Set(
      items
        .filter((item) => item.type === relevantType)
        .map((item) => item.user_id)
        .filter(Boolean)
    );
    return users.filter(
      (u) => relevantUserIds.has(u.id) || relevantUserIds.has(u.externalId)
    );
  }, [selectedTransaction, items, users]);

  const userDropdownOptions = useMemo(
    () => userOptions.map((u) => u.externalId || u.id),
    [userOptions]
  );

  const userOptionToId = useMemo(() => {
    const map: Record<string, string> = {};
    userOptions.forEach((u) => {
      map[u.externalId || u.id] = u.id;
    });
    return map;
  }, [userOptions]);

  const selectedUserLabel = useMemo(() => {
    const user = userOptions.find((u) => u.id === selectedUserId);
    if (!user) return '';
    return user.externalId || user.id;
  }, [userOptions, selectedUserId]);

  const canSubmit =
    selectedTransactionId !== '' &&
    selectedUserId !== '' &&
    !postAllocationsMutation.isPending;

  const handleSelectTransaction = (option: string) => {
    const txn = optionToTxn[option];
    setSelectedTransactionId(txn.id ?? '');
    setSelectedUserId('');
  };

  const handleSelectUser = (option: string) => {
    setSelectedUserId(userOptionToId[option] ?? '');
  };

  const handleSubmit = async () => {
    if (!selectedTransaction) return;

    const isPositive = Number(selectedTransaction.amount) > 0;
    const allocationType = isPositive ? 'invoice_payin' : 'invoice_payout';

    try {
      await postAllocationsMutation.mutateAsync({
        id: selectedTransactionId,
        data: {
          version: selectedTransaction.version,
          allocation_updates: [
            {
              op: 'add',
              invoice_id: invoiceId,
              amount: selectedTransaction.amount,
              user: { id: selectedUserId },
              type: allocationType,
            },
          ],
        },
      });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['/transactions'] }),
        queryClient.invalidateQueries({ queryKey: ['/invoices'] }),
      ]);
      onClose();
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : 'Failed to allocate transaction.';
      showToast(<Toast type="error" message={message} />);
    }
  };

  return (
    <Modal
      data-testid="allocate-transaction-modal"
      onClose={onClose}
      width="sm"
      header={<div className="text-main">Allocate Transaction</div>}
      body={
        <div className="flex flex-col space-y-f2">
          <ModalField label="Transaction" labelWidth="w-f12">
            <ModalDropdown
              data-testid="transaction-select"
              options={transactionOptions}
              value={selectedTransactionLabel}
              onChange={handleSelectTransaction}
              placeholder="Select a transaction"
            />
          </ModalField>
          {selectedTransactionId && (
            <ModalField label="User" labelWidth="w-f12">
              <ModalDropdown
                data-testid="user-select"
                options={userDropdownOptions}
                value={selectedUserLabel}
                onChange={handleSelectUser}
                placeholder="Select a user"
              />
            </ModalField>
          )}
        </div>
      }
      footer={
        <div className="flex flex-row justify-between w-full">
          <FormButtons
            type="secondary"
            cancelButtonLabel="Cancel"
            submitButtonLabel="Allocate"
            onCancel={onClose}
            onSubmit={handleSubmit}
            disabled={!canSubmit}
          />
        </div>
      }
    />
  );
};
